import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

const API = "/api";
const AQUAMARINE = "#5FE3D0";
const BRAND_BLUE = "#1E3A8A"; // navy from logo

export default function TutorProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API}/tutors/public`)
      .then((r) => setProfiles(r.data.items || []))
      .catch(() => setProfiles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <h1
            className="text-2xl font-bold tracking-tight"
            style={{ color: BRAND_BLUE }}
            data-testid="tutor-profiles-title"
          >
            LanguaConnect English Class
          </h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/student-login")}
              data-testid="student-login-btn"
            >
              Student Login
            </Button>
            <Button
              onClick={() => navigate("/book-trial")}
              style={{ backgroundColor: AQUAMARINE, color: "#0F172A" }}
              className="hover:opacity-90"
              data-testid="book-trial-btn"
            >
              Book Free Trial
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2
          className="text-4xl sm:text-5xl font-bold text-center mb-12"
          style={{ color: BRAND_BLUE }}
          data-testid="our-teaching-experts-heading"
        >
          Our Teaching Experts
        </h2>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : profiles.length === 0 ? (
          <p className="text-center text-slate-500" data-testid="no-tutors-empty">
            No tutor profiles available yet. Please check back soon!
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {profiles.map((p) => (
              <TutorCard key={p.id} profile={p} onSelect={() => navigate(`/tutors/${p.id}`)} />
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-slate-200 py-6 text-center text-sm text-slate-500">
        © {new Date().getFullYear()} LanguaConnect English Class
      </footer>
    </div>
  );
}

function TutorCard({ profile, onSelect }) {
  const isBlue = (profile.card_color || "blue") === "blue";
  const bgStyle = isBlue ? { backgroundColor: BRAND_BLUE } : { backgroundColor: "#FFFFFF" };
  const textColor = isBlue ? "text-white" : "text-slate-900";

  return (
    <button
      onClick={onSelect}
      className="text-left rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group border border-slate-200"
      style={bgStyle}
      data-testid={`tutor-card-${profile.id}`}
    >
      <div className="p-6 flex flex-col items-center">
        <div className="w-32 h-40 rounded-full overflow-hidden ring-4 ring-white/50 mb-4 bg-slate-100 flex items-center justify-center"
             style={{ borderRadius: "50%" }}>
          {profile.photo_url ? (
            <img
              src={profile.photo_url}
              alt={profile.name}
              className="w-full h-full object-cover"
              style={{ borderRadius: "50%" }}
            />
          ) : (
            <span className="text-3xl text-slate-400">{profile.name?.charAt(0)}</span>
          )}
        </div>
        <h3 className={`text-2xl font-bold tracking-wide ${textColor}`} data-testid={`tutor-name-${profile.id}`}>
          {profile.name}
        </h3>
      </div>

      <div className={`px-6 pb-6 ${isBlue ? "bg-white" : ""}`}>
        <div className={`text-sm text-slate-700 leading-relaxed whitespace-pre-wrap line-clamp-[12]`}>
          {profile.tagline ? (
            <p className="font-semibold text-slate-900 mb-2">{profile.tagline}</p>
          ) : null}
          {profile.bio || (
            <span className="text-slate-500 italic">
              {profile.specialization || "Experienced English teacher."}
            </span>
          )}
        </div>
        <div className="mt-4 text-center text-xs font-semibold uppercase tracking-wider text-slate-500 group-hover:text-slate-900 transition-colors">
          View profile &amp; book →
        </div>
      </div>
    </button>
  );
}
